import { h, ref, VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import {
    VvButton,
    VvButtonGroup,
    VvInputText,
} from '@volverjs/ui-vue/components'
import { useDialog } from './useDialog'

export const useDialogConfirm = () => {
    const { openDialog } = useDialog()
    const { t: $t } = useI18n({
        useScope: 'global',
    })

    /**
     * Ask before doing something, in three parts that a person reads in order.
     *
     * `title` is what is about to happen, short, in the dialog header.
     * `questionLabel` is the question itself. `descriptionLabel` is what it
     * costs: the consequences, secondary, under the question. Splitting them
     * is the whole point: a confirm that puts question and consequences in one
     * paragraph is read as a wall and answered without being read, which is
     * the opposite of what a confirm is for.
     */
    const openDialogConfirm = ({
        emitReject = false,
        confirmLabel,
        cancelLabel,
        questionLabel,
        descriptionLabel,
        onlyConfirm = false,
        passphrase,
        passphraseLabel,
        passphraseHint,
        title,
    }: {
        emitReject?: boolean
        confirmLabel?: string
        cancelLabel?: string
        questionLabel?: string
        descriptionLabel?: string
        onlyConfirm?: boolean
        passphrase?: string
        passphraseLabel?: string
        passphraseHint?: string
        title?: string
    } = {}) => {
        return new Promise<boolean>((resolve, reject) => {
            const passphraseText = ref<string>()
            const isOpen = openDialog(
                {
                    role: 'alertdialog',
                    size: 'small',
                    keepOpen: true,
                    title,
                },
                {
                    default: () =>
                        h('div', [
                            h(
                                'div',
                                {
                                    class: descriptionLabel ? '' : 'mb-sm',
                                    style: 'white-space: pre-line',
                                },
                                questionLabel ?? $t('message.confirm'),
                            ),
                            descriptionLabel
                                ? h(
                                      'p',
                                      {
                                          class: 'text-14 text-word-2 mt-xs mb-sm',
                                          style: 'white-space: pre-line',
                                      },
                                      descriptionLabel,
                                  )
                                : undefined,
                            passphrase
                                ? h(VvInputText, {
                                      modelValue: passphraseText.value,
                                      'onUpdate:modelValue': (
                                          value: string,
                                      ) => {
                                          passphraseText.value = value
                                      },
                                      type: 'text',
                                      name: 'passphrase',
                                      class: 'mb-0',
                                      floating: true,
                                      label:
                                          passphraseLabel ??
                                          $t('label.passphrase'),
                                      hintLabel:
                                          passphraseHint ??
                                          $t('hint.passphrase', { passphrase }),
                                  })
                                : undefined,
                        ]),
                    footer: () =>
                        h(VvButtonGroup, () => {
                            const toReturn: VNode[] = []
                            if (!onlyConfirm) {
                                toReturn.push(
                                    h(VvButton, {
                                        label:
                                            cancelLabel ?? $t('action.cancel'),
                                        modifiers: 'ghost',
                                        onClick: () => {
                                            if (emitReject) {
                                                reject()
                                            }
                                            resolve(false)
                                            isOpen.value = false
                                        },
                                    }),
                                )
                            }
                            toReturn.push(
                                h(VvButton, {
                                    label: confirmLabel ?? $t('action.proceed'),
                                    disabled: passphrase
                                        ? passphrase !== passphraseText.value
                                        : false,
                                    onClick: () => {
                                        resolve(true)
                                        isOpen.value = false
                                    },
                                }),
                            )
                            return toReturn
                        }),
                },
            )
        })
    }

    return { openDialogConfirm }
}
